/**
 * RimWorld-lite body simulation: 6 parts with independent HP pools,
 * crippled flags on limbs, death when head or torso HP hits 0.
 * Attached to Player + Guard by extending their interfaces with `body`.
 *
 * See CLAUDE.md > Body Simulation. Headless — no PixiJS.
 */

export type BodyPart =
  | 'head'
  | 'torso'
  | 'leftArm'
  | 'rightArm'
  | 'leftLeg'
  | 'rightLeg';

export const BODY_PARTS: readonly BodyPart[] = [
  'head',
  'torso',
  'leftArm',
  'rightArm',
  'leftLeg',
  'rightLeg',
] as const;

/** Damage severity classification returned by `applyDamage`. */
export type Severity = 'graze' | 'wound' | 'cripple' | 'kill';

export interface BodyState {
  hp: Record<BodyPart, number>;
  hpMax: Record<BodyPart, number>;
  crippled: Record<BodyPart, boolean>;
  /** HP lost per second from bleeding wounds. */
  bleedPerSec: number;
  dead: boolean;
}

const HP_HEAD = 50;
const HP_TORSO = 100;
const HP_ARM = 40;
const HP_LEG = 60;

/** Weighted limb selection: torso > legs > arms > head. */
const LIMB_WEIGHTS: ReadonlyArray<readonly [BodyPart, number]> = [
  ['torso', 40],
  ['leftLeg', 15],
  ['rightLeg', 15],
  ['leftArm', 12],
  ['rightArm', 12],
  ['head', 6],
];
const LIMB_WEIGHT_TOTAL = LIMB_WEIGHTS.reduce((s, [, w]) => s + w, 0);

export function createHumanoidBody(): BodyState {
  return {
    hp: { head: HP_HEAD, torso: HP_TORSO, leftArm: HP_ARM, rightArm: HP_ARM, leftLeg: HP_LEG, rightLeg: HP_LEG },
    hpMax: { head: HP_HEAD, torso: HP_TORSO, leftArm: HP_ARM, rightArm: HP_ARM, leftLeg: HP_LEG, rightLeg: HP_LEG },
    crippled: { head: false, torso: false, leftArm: false, rightArm: false, leftLeg: false, rightLeg: false },
    bleedPerSec: 0,
    dead: false,
  };
}

/** Pick a limb using the weighted distribution above. `rng` in [0, 1). */
export function pickLimb(rng: () => number = Math.random): BodyPart {
  let roll = rng() * LIMB_WEIGHT_TOTAL;
  for (const [part, w] of LIMB_WEIGHTS) {
    if (roll < w) return part;
    roll -= w;
  }
  return 'torso';
}

/**
 * Apply damage to one body part. Sets crippled on arms/legs when their HP
 * hits 0; sets dead=true when head or torso HP hits 0. Adds bleeding for
 * damage above 15% of the part's hpMax.
 */
export function applyDamage(body: BodyState, part: BodyPart, amount: number): Severity {
  if (body.dead || amount <= 0) return 'graze';
  const before = body.hp[part];
  const dealt = Math.min(before, amount);
  body.hp[part] = before - dealt;
  const dmgPct = amount / body.hpMax[part];

  // Bleeding: overflow damage still contributes so a headshot with 100 dmg
  // adds a lot of bleed even if the part cap is 50.
  if (amount >= body.hpMax[part] * 0.15) {
    body.bleedPerSec += amount * 0.1;
  }

  if (body.hp[part] === 0) {
    if (part === 'head' || part === 'torso') {
      body.dead = true;
      return 'kill';
    }
    body.crippled[part] = true;
    return 'cripple';
  }
  if (dmgPct >= 0.5) return 'wound';
  return 'graze';
}

/** Advance bleeding damage — applied to the torso for now (phase 3 shortcut). */
export function tickBleed(body: BodyState, dt: number): void {
  if (body.dead || body.bleedPerSec <= 0) return;
  const loss = body.bleedPerSec * dt;
  body.hp.torso = Math.max(0, body.hp.torso - loss);
  if (body.hp.torso === 0) body.dead = true;
}

/** Overall vitals in [0, 1] — average of torso + head HP fractions. */
export function vitalsPercent(body: BodyState): number {
  if (body.dead) return 0;
  return (body.hp.torso / body.hpMax.torso + body.hp.head / body.hpMax.head) / 2;
}

/** Speed multiplier from leg crippling. Both legs crippled ≡ can't move. */
export function movementMultiplier(body: BodyState): number {
  if (body.dead) return 0;
  const l = body.crippled.leftLeg;
  const r = body.crippled.rightLeg;
  if (l && r) return 0;
  if (l || r) return 0.5;
  return 1;
}

/** True if either arm is crippled — used later to gate two-handed weapons. */
export function hasCrippledArm(body: BodyState): boolean {
  return body.crippled.leftArm || body.crippled.rightArm;
}

/**
 * Can this body physically operate a weapon whose grip needs `twoHanded` arms?
 * A crippled arm disables what needs it: a one-handed weapon (pistol, knife,
 * fists) needs at least ONE working arm; a two-handed weapon (SMG) needs BOTH.
 * Both arms gone → can't fire anything. Applies to guards, the janitor, and the
 * player alike — take out the shooting arm and the shot doesn't happen.
 */
export function canOperateWeapon(body: BodyState, twoHanded: boolean): boolean {
  if (body.dead) return false;
  const left = !body.crippled.leftArm;
  const right = !body.crippled.rightArm;
  return twoHanded ? left && right : left || right;
}

/**
 * Incapacitated ≠ dead. Body is on the ground and can't act, but torso +
 * head still have HP. RimWorld-style "downed" state — triggers the drop-
 * on-side tilt animation without ending the entity.
 */
export function isIncapacitated(body: BodyState): boolean {
  if (body.dead) return true;
  // Both legs gone — can't stand.
  if (body.crippled.leftLeg && body.crippled.rightLeg) return true;
  // Vitals below 20% — collapsed from shock/bleed.
  if (vitalsPercent(body) < 0.2) return true;
  return false;
}

/**
 * True when a body is visibly hurt — bleeding, missing a limb, or below full
 * vitals. Used by blood-trail tracking to decide whether the entity at the end
 * of a trail is a casualty worth patching up.
 */
export function isWounded(body: BodyState): boolean {
  if (body.dead) return false;
  if (body.bleedPerSec > 0) return true;
  if (body.crippled.leftArm || body.crippled.rightArm) return true;
  if (body.crippled.leftLeg || body.crippled.rightLeg) return true;
  return vitalsPercent(body) < 0.9;
}

/**
 * Apply a health pack: restore `perLimb` HP to every part (capped at max),
 * clear crippled flags, and stop bleeding. A partial heal (unlike `fullHeal`)
 * so a single pack doesn't fully reset a badly-wounded body. Returns total HP
 * restored.
 */
export function applyHealthPack(body: BodyState, perLimb: number): number {
  if (body.dead) return 0;
  let healed = 0;
  for (const p of BODY_PARTS) {
    const before = body.hp[p];
    body.hp[p] = Math.min(body.hpMax[p], before + perLimb);
    body.crippled[p] = false;
    healed += body.hp[p] - before;
  }
  body.bleedPerSec = 0;
  return healed;
}

/** Full-heal reset — used by the armory heal-station tile. */
export function fullHeal(body: BodyState): void {
  for (const p of BODY_PARTS) {
    body.hp[p] = body.hpMax[p];
    body.crippled[p] = false;
  }
  body.bleedPerSec = 0;
  body.dead = false;
}
